/**
 * UI component for requesting microphone permission.
 *
 * This component:
 * - Shows UI only when permission is needed
 * - Handles permission errors with user-friendly messages
 * - Provides troubleshooting help after multiple failures
 * - Optionally allows audio file upload as fallback
 *
 * Standards compliance:
 * - Uses getUserMedia for permission request (standard)
 * - Provides clear error messages for each error type
 * - Respects browser permission model (no server-side storage)
 */
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@repo/videa-components/src/index';
import {
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  Mic,
  RefreshCw,
} from 'lucide-react';

import { useMicrophoneContext } from '../../contexts/MicrophoneContext';
import { AudioFileUpload } from '../microphone/AudioFileUpload';

/**
 * Outcome of a microphone permission request.
 * - granted: user allowed access
 * - denied: user explicitly denied (clicked Block)
 * - timeout: prompt was ignored or system blocked it (e.g. macOS)
 * - prompt-blocked: browser did not show the prompt (e.g. previously dismissed)
 * - error: hardware/other failure (no mic, in use, etc.)
 */
export type MicrophonePermissionOutcome =
  | 'granted'
  | 'denied'
  | 'timeout'
  | 'prompt-blocked'
  | 'error';

interface MicrophonePermissionFlowProps {
  /**
   * Called when permission is successfully granted.
   * Optional because permission state is automatically managed via Jotai atoms
   * (useMicrophoneContext().hasPermission) - parent components can simply use the
   * context for conditional rendering instead of relying on this callback.
   */
  onPermissionGranted?: () => void;
  /**
   * Called after each permission request with the outcome. Use for analytics.
   * Distinguishes user denial from timeout, prompt-blocked, and hardware errors.
   */
  onPermissionOutcome?: (outcome: MicrophonePermissionOutcome) => void;
  /** Called when user selects an audio file (optional fallback) */
  onAudioFileSelected?: (file: File, blobUrl: string) => void;
  /** Whether to show audio upload as fallback option */
  isAudioUploadEnabled?: boolean;
  /** Whether to show developer tools/options */
  showDevTools?: boolean;
}

/** Default message when permission is denied without an error object */
const DEFAULT_DENIED_MESSAGE =
  'Microphone access was denied. Please allow access in your browser settings to continue.';

/**
 * Get user-friendly error message based on error type.
 *
 * @param error - The error object, or null
 * @param isDenied - Whether permission state is 'denied' (used for fallback message)
 */
const getErrorMessage = (error: Error | null, isDenied: boolean): string => {
  if (!error) {
    // When permission is denied but no error (e.g., page loaded with permission already blocked),
    // show the default denied message
    return isDenied ? DEFAULT_DENIED_MESSAGE : '';
  }

  // Handle blocked prompt error - browser won't show permission dialog
  if (error.name === 'MicrophonePermissionPromptBlockedError') {
    return error.message;
  }

  if (
    error.name === 'NotAllowedError' ||
    error.name === 'PermissionDeniedError'
  ) {
    return DEFAULT_DENIED_MESSAGE;
  }
  if (error.name === 'NotFoundError') {
    return 'No microphone found. Please connect a microphone and try again.';
  }
  if (error.name === 'NotReadableError') {
    return 'Could not access the microphone. It may be in use by another application.';
  }
  if (error.name === 'OverconstrainedError') {
    return 'Your microphone does not support the required settings.';
  }
  if (error.name === 'AbortError') {
    return 'Microphone access was interrupted. Please try again.';
  }
  if (error.name === 'SecurityError') {
    return 'Microphone access is blocked by browser security settings.';
  }

  return `Microphone error: ${error.message}`;
};

export const MicrophonePermissionFlow: FC<MicrophonePermissionFlowProps> = ({
  onPermissionGranted,
  onPermissionOutcome,
  onAudioFileSelected,
  isAudioUploadEnabled = false,
  showDevTools = false,
}) => {
  const [retryCount, setRetryCount] = useState(0);

  // Use the microphone context (browser API source of truth)
  const {
    permissionState,
    hasPermission: hasAccess,
    requestPermission,
    error,
    isLoading,
  } = useMicrophoneContext();

  // Auto-notify when permission is granted
  useEffect(() => {
    if (hasAccess) {
      onPermissionGranted?.();
    }
  }, [hasAccess, onPermissionGranted]);

  // Handle permission request
  const handleRequestPermission = async () => {
    const result = await requestPermission();

    const outcome: MicrophonePermissionOutcome = result.granted
      ? 'granted'
      : result.reason;
    onPermissionOutcome?.(outcome);

    if (result.granted) {
      setRetryCount(0);
    } else {
      setRetryCount((prev) => prev + 1);
    }
  };

  // Don't render if we already have permission
  if (hasAccess) {
    return null;
  }

  const isDenied = permissionState === 'denied';
  const hasError = error !== null;
  const showTroubleshooting = retryCount >= 3;
  const isPromptBlocked =
    error?.name === 'MicrophonePermissionPromptBlockedError';

  // Handle page refresh for prompt blocked error
  const handleRefreshPage = () => {
    window.location.reload();
  };

  return (
    <div className="vai-flex-col vai-p-4 vai-w-full">
      <div className="vai-flex vai-p-4 vai-w-full vai-bg-blue-50 vai-border vai-border-blue-200 vai-rounded-lg">
        <div className="vai-flex vai-items-start vai-gap-3 vai-w-full">
          <div className="vai-flex-1">
            <h3 className="vai-text-sm vai-font-bold vai-mb-2">
              Microphone Access Required
            </h3>
            <p className="vai-text-sm vai-mb-4">
              To record audio notes, we need access to your microphone.
            </p>

            {/* Error Messages */}
            {isDenied && (
              <div className="vai-p-3 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded vai-mb-4">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <AlertTriangle className="vai-w-4 vai-h-4 vai-text-red-500" />
                  <p className="vai-text-sm vai-text-red-700">
                    {getErrorMessage(error, isDenied)}
                  </p>
                </div>
              </div>
            )}

            {hasError && !isDenied && (
              <div className="vai-p-3 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded vai-mb-4">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <AlertCircle className="vai-w-4 vai-h-4 vai-text-yellow-600" />
                  <p className="vai-text-sm vai-text-yellow-700">
                    {getErrorMessage(error, isDenied)}
                  </p>
                </div>
              </div>
            )}

            {/* Permission Button */}
            <div className="vai-flex vai-justify-end vai-gap-2">
              {isPromptBlocked && (
                <Button
                  onClick={handleRefreshPage}
                  variant="outline"
                  className="vai-flex vai-gap-2 vai-font-medium"
                >
                  <RefreshCw className="vai-w-4 vai-h-4" />
                  Refresh Page
                </Button>
              )}

              <Button
                onClick={handleRequestPermission}
                disabled={isLoading}
                className="vai-flex vai-gap-2 vai-font-medium"
              >
                <Mic className="vai-w-4 vai-h-4" />
                {isLoading
                  ? 'Requesting Access...'
                  : retryCount > 0
                    ? 'Try Again'
                    : 'Allow Microphone Access'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Troubleshooting Help */}
      {showTroubleshooting && (
        <div className="vai-p-3 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded vai-mt-4 vai-w-full">
          <div className="vai-flex vai-items-start vai-gap-3 vai-w-full">
            <div className="vai-flex-1">
              <h3 className="vai-text-sm vai-font-bold vai-mb-2">
                Having trouble?
              </h3>
              <p className="vai-text-sm vai-mb-4">
                You may need to manually enable microphone access in your
                browser settings. Click the icon in your browser&apos;s address
                bar or check your browser&apos;s settings.
              </p>
              <div className="vai-flex vai-justify-end">
                <a
                  href="https://support.google.com/chrome/answer/2693767"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vai-inline-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-text-sm vai-font-medium vai-text-gray-700 vai-bg-white vai-border vai-border-gray-300 vai-rounded-md vai-shadow-sm hover:vai-bg-gray-50 focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-offset-2 focus:vai-ring-blue-500"
                >
                  <ExternalLink className="vai-w-3 vai-h-3" />
                  Browser Microphone Help
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Upload Fallback */}
      {isAudioUploadEnabled && onAudioFileSelected && showDevTools && (
        <div className="vai-mt-4 vai-pt-4 vai-border-t vai-border-gray-200 vai-w-full vai-flex vai-flex-col vai-items-center">
          <p className="vai-text-sm vai-text-gray-500 vai-mb-2">
            Or upload a pre-recorded audio file
          </p>
          <AudioFileUpload onFileSelected={onAudioFileSelected} />
        </div>
      )}
    </div>
  );
};
